import React, {
  useCallback,
  useMemo,
  useState
} from "react"
import { Provider } from "react-redux";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import { AlertContainer } from "@ifrc-go/ui";
import {
  AlertContext,
  type AlertContextProps,
  type AlertParams,
} from '@ifrc-go/ui/contexts';
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { unique } from "@togglecorp/fujs";

import AlertReader from "./components/AlertReader";
import AuthFetcher from "./components/AuthFetcher";
import GlobalFooter from "./components/GlobalFooter";
import Navbar from "./components/Navbar";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import useAxiosInterceptor from "./hooks/useAxiosInterceptor";
import { store } from "./redux/store";
import ScrollToTop from "./utils/scrollToTop";

import styles from "./styles.module.css";

if (import.meta.env.PROD && import.meta.env.APP_SENTRY_DSN) {
  Sentry.init({
    environment: import.meta.env.APP_ENVIRONMENT,
    dsn: import.meta.env.APP_SENTRY_DSN,
    tracesSampleRate: import.meta.env.APP_SENTRY_TRACES_SAMPLE_RATE,

    release: import.meta.env.APP_COMMIT_HASH || "0.local",
    integrations: [new BrowserTracing()],
  });
}

const Home = React.lazy(() => import("./views/LandingPage"));
const Login = React.lazy(() => import("./views/Login"));
const SurveyWizard = React.lazy(() => import("./views/SurveyWizard"));
const ApiKeys = React.lazy(() => import("./views/APIKeys"));
const Faq = React.lazy(() => import("./views/Faq"));
const NotFound = React.lazy(() => import("./views/FourHundredFour"));
const SharedLinkLandingPage = React.lazy(() => import("./views/SharedLink"));
const Surveys = React.lazy(() => import("./views/Surveys"));

function App() {
  // NOTE: we should not use window.global
  window.global = window;

  useAxiosInterceptor();

  const [alerts, setAlerts] = useState<AlertParams[]>([]);

  const addAlert = useCallback((alert: AlertParams) => {
    setAlerts((prevAlerts) => unique(
      [...prevAlerts, alert],
      (a) => a.name,
    ) ?? prevAlerts);
  }, [setAlerts]);

  const removeAlert = useCallback((name: AlertParams['name']) => {
    setAlerts((prevAlerts) => {
      const i = prevAlerts.findIndex((a) => a.name === name);
      if (i === -1) {
        return prevAlerts;
      }

      const newAlerts = [...prevAlerts];
      newAlerts.splice(i, 1);

      return newAlerts;
    });
  }, [setAlerts]);

  const updateAlert = useCallback((name: AlertParams['name'], paramsWithoutName: Omit<AlertParams, 'name'>) => {
    setAlerts((prevAlerts) => {
      const i = prevAlerts.findIndex((a) => a.name === name);
      if (i === -1) {
        return prevAlerts;
      }

      const updatedAlert = {
        ...prevAlerts[i],
        paramsWithoutName,
      };

      const newAlerts = [...prevAlerts];
      newAlerts.splice(i, 1, updatedAlert);

      return newAlerts;
    });
  }, [setAlerts]);

  const alertContextValue: AlertContextProps = useMemo(() => ({
    alerts,
    addAlert,
    updateAlert,
    removeAlert,
  }), [alerts, addAlert, updateAlert, removeAlert]);

  return (
    <Provider
      store={store}
    >
      <AlertContext.Provider value={alertContextValue}>
        <AlertReader />
        <AuthFetcher />
        <div className={styles.surveyDesignerApp}>
          <Router>
            <ScrollToTop />
            <Navbar />
            <div className={styles.currentRoute}>
              <Switch>
                <Route path="/permalink/login">
                  <Redirect to="/login" />
                </Route>
                <Route path="/permalink/logout-callback">
                  <Redirect to="/" />
                </Route>
                <Route path="/permalink/login-callback">
                  <Redirect to="/" />
                </Route>
                  {/* NOTE: Handle authorize cancellation */}
                  {/* The exact URL is permalink/account/provider/callback?error=unknown&error_process=login */}
                <Route path="/permalink/account/provider/callback">
                  <Redirect to="/" />
                </Route>
                <PublicRoute
                  exact
                  path="/"
                >
                    <Home />
                </PublicRoute>
                <PublicRoute
                  path="/login"
                >
                  <Login />
                </PublicRoute>
                <PrivateRoute
                  exact
                  path="/design/:step"
                >
                  <SurveyWizard />
                </PrivateRoute>
                <PrivateRoute path="/surveys" >
                  <Surveys />
                </PrivateRoute>
                <PrivateRoute path="/api-keys">
                  <ApiKeys />
                </PrivateRoute>
                <Route path="/help">
                  <Faq />
                </Route>
                <PrivateRoute path="/survey/copy/:uuid">
                  <SharedLinkLandingPage />
                </PrivateRoute>
                <Route component={NotFound} />
              </Switch>
            </div>
            <GlobalFooter />
          </Router>
          <AlertContainer />
        </div>
      </AlertContext.Provider>
    </Provider>
  );
}

export default App;
